// rowOps — generic row upsert/soft-delete helpers (renamed from supabaseRow.js;
// the transport has been SDK-only since the Supabase-era pattern it replaced). Bind via .call('upsertRow', ...)
// from any DOMQL element so the active fetch context (config.fetch — the SDK
// adapter, see config.js) is reused.

const _logErr = (scope, err) => console.error(`[${scope}]`, err?.message || err)

// Date.now() is 13 digits in 2026; legacy autoincrement ids are <13 digits for
// the foreseeable future. Anything in [FLOOR, CEILING) is a client-minted
// optimistic id. The CEILING (16 digits) keeps the numeric-string branch from
// swallowing a long all-digit server id — a Mongo ObjectId is 24 hex chars and
// may legally be all digits.
const OPTIMISTIC_ID_FLOOR = 1_000_000_000_000
const OPTIMISTIC_ID_CEILING = 1_000_000_000_000_000

// WS-ROWOPS-OPTIMISTIC-ID-STRING-1 (mirrors the calendar fix in
// packages/calendar/functions/saveEvent.js —
// WS-CALENDAR-SAVEEVENT-OPTIMISTIC-ID-DUPES-1): the old test was
// `typeof id !== 'number' || id >= FLOOR`. Post-cutover the server exposes a
// 24-hex ObjectId STRING id, so "not a number" stopped meaning "optimistic":
// `upsertRow` stripped the id off a real row (INSERTing a duplicate) and
// `softDeleteRow` returned true without writing. A server-exposed id — string,
// or legacy number below the floor — is PERSISTED. An id is optimistic ONLY in
// the CLIENT's own shape: absent, or a numeric (or numeric-string) Date.now()
// value inside the optimistic band.
const _isOptimisticNumber = (n) =>
  Number.isFinite(n) && n >= OPTIMISTIC_ID_FLOOR && n < OPTIMISTIC_ID_CEILING

export const isOptimisticId = (id) => {
  if (id == null || id === '') return true
  if (typeof id === 'number') return Number.isFinite(id) ? _isOptimisticNumber(id) : true
  if (typeof id === 'string') {
    const trimmed = id.trim()
    // A Date.now() id that arrived stringified is still optimistic; any other
    // string (24-hex ObjectId, uuid, SMBLS-N slug, …) is a real server id.
    if (!/^\d+$/.test(trimmed)) return false
    return _isOptimisticNumber(Number(trimmed))
  }
  return true
}

export const upsertRow = async function ({ from, row }) {
  const fetch = this.context?.fetch
  if (!fetch?.upsert) { _logErr('upsertRow', 'context.fetch.upsert missing'); return null }
  const data = isOptimisticId(row?.id) ? { ...row, id: undefined } : row
  try {
    const result = await fetch.upsert({ from, data })
    if (result?.error) { _logErr('upsertRow', result.error); return null }
    return result?.data?.[0]?.id ?? null
  } catch (e) { _logErr('upsertRow', e); return null }
}

export const softDeleteRow = async function ({ from, id }) {
  if (isOptimisticId(id)) return true
  const fetch = this.context?.fetch
  if (!fetch?.update) { _logErr('softDeleteRow', 'context.fetch.update missing'); return false }
  try {
    const result = await fetch.update({
      from,
      params: { id },
      data: { deleted: true, dirty: true, updated_at: new Date().toISOString() }
    })
    if (result?.error) { _logErr('softDeleteRow', result.error); return false }
    return true
  } catch (e) { _logErr('softDeleteRow', e); return false }
}
